import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dimensions,
  Pressable,
  Share,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { CalendarList, LocaleConfig } from "react-native-calendars";
import { convertDate, convertToAMPM, getAllDates } from "../../utils/timeUtils";
import { RootState, useSelector } from "../../redux/store";
import { Colors } from "../../globalStyles/ColorConfig";
import GlobalStyles from "../../globalStyles/globalStyles";
import ModalComponent from "../ModalComponent";
import dimensions from "../../globalStyles/dimensions";
import CustomComponent from "../../components/CustomComponent";
import ExperienceIcons from "../../assets/Icons/ExperienceIcons";
import ShareCardModal from "./ShareCardModal";
import { BlurView } from "expo-blur";

type MarkedDatesType = {
  [date: string]: {
    selected: boolean;
    selectedColor: string;
    textColor: string;
  };
};

LocaleConfig.locales["en"] = {
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  monthNamesShort: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  dayNames: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  dayNamesShort: ["S", "M", "T", "W", "T", "F", "S"],
  today: "Today",
};

LocaleConfig.defaultLocale = "en";

function getTodayDate(): string | null {
  try {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const day = today.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
}

const DateModal = ({
  isDialog = false,
  isVisible,
  toggleModal,
  date,
}: {
  isDialog?: boolean;
  isVisible: boolean;
  toggleModal: () => void;
  date?: any;
}) => {


  const {
    experience: experienceData,
    availibility,
    inboxUser: { receiverChatRoomId, receiverName, receiverUserId },
  } = useSelector((state: RootState) => state.experience);

  const currentDate = useMemo(() => getTodayDate(), []);
  const [selectedDate, setSelectedDate] = useState<string | null>(date || "");
  const [isCard, setIsCard] = useState(false);
  const [isSlotAvailable, setIsSlotAvailable] = useState<any>({
    slot_id: "",
    startTime: "",
    endTime: "",
    slots: 0,
  });

  useEffect(() => {
    setSelectedDate(date || "");
  }, [date]);

  const isReceiverInfoEmpty =
    !receiverChatRoomId || !receiverName || !receiverUserId;

  const data = {
    _id: experienceData?.data?._id,
    title: experienceData?.data?.title,
    place: experienceData?.data?.location?.name,
    timing: `${convertToAMPM(isSlotAvailable?.startTime)} - ${convertToAMPM(
      isSlotAvailable?.endTime
    )}`,
    date: convertDate(selectedDate),
    slot_id:  isSlotAvailable?.slot_id,
    price: experienceData?.data?.price
  };

  useEffect(()=>{
    const selectedDateSlots: any = availibility?.find((schedule: any) => {
      return schedule?.dayString === selectedDate;
    });

    if (selectedDateSlots) {
      setIsSlotAvailable({
        startTime: selectedDateSlots?.startTime,
        endTime: selectedDateSlots?.endTime,
        slots: selectedDateSlots?.slots,
        slot_id: selectedDateSlots.slot_id
      });
    } else {
      setIsSlotAvailable({
        startTime: "",
        endTime: "",
        slots: 0,
        slot_id: ""
      });
    }
  },[selectedDate])

  const formattedDates = useMemo(() => {
    return availibility?.map((schedule: any) => {
      return schedule?.dayString;
    });
  }, [availibility]);

  const getMarked = (): MarkedDatesType => {
    const markedDates: MarkedDatesType = {};

    if (selectedDate) {
      markedDates[selectedDate] = {
        customStyles: {
          container: {
            backgroundColor: Colors.green10,
          },
          text: {
            color: Colors.white10,
            fontWeight: "bold",
          },
        },
      };
    }

    const allDates = getAllDates(); // Implement this function to get all possible dates
    for (const date of allDates) {
      if (!formattedDates?.includes(date)) {
        markedDates[date] = {
          disabled: true,
          disableTouchEvent: true,
        };
      }
    }

    return markedDates;
  };

  const onDayPress = (day: { dateString: string }) => {
    setSelectedDate(day.dateString);

    // // Calculate slots immediately when a date is pressed
    const selectedDateSlots = availibility?.find((schedule: any) => {
      return schedule?.dayString === day?.dateString;
    });

    if (selectedDateSlots) {
      setIsSlotAvailable({
        startTime: selectedDateSlots?.startTime,
        endTime: selectedDateSlots?.endTime,
        slots: selectedDateSlots?.slots,
      });
    } else {
      setIsSlotAvailable({
        startTime: "",
        endTime: "",
        slots: 0,
      });
    }
  };

  const renderHeader = (date: Date) => {
    const monthText = LocaleConfig.locales.en.monthNames[date.getMonth()];
    return (
      <View
        style={{
          position: "relative",
          paddingTop: 10,
          marginBottom: 10,
          display: "flex",
          width: "100%",
          borderTopColor: Colors.black30,
          borderTopWidth: 0.5,
        }}
      >
        <Text
          style={[
            GlobalStyles.title3,
            {
              textAlign: "left",
              fontSize: 24,
              color: Colors.darkGreen,
              opacity: 0.9,
              fontFamily: "EBGaramondSemiBold",
            },
          ]}
        >
          {monthText}
        </Text>
      </View>
    );
  };

  const continuePress = () => {
    setIsCard(false);
  };

  const onShareInbox = async () => {
    try {
    } catch (error) {}
  };

  const onShare = async () => {
    try {
      const result = await Share.share({
        message: data.title,
      });
      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error: any) {
      Alert.alert(error.message);
    }
  };

  return (
    <ModalComponent isVisible={isVisible} toggleModal={toggleModal}>
      <ShareCardModal
        isDialog={false}
        isVisible={isCard}
        toggleModal={continuePress}
        experienceData={experienceData}
        receiverName={receiverName}
        isReceiverInfoEmpty={isReceiverInfoEmpty}
        onShare={onShare}
        data={data}
      />
      <BlurView
        intensity={100}
        style={{
          flex: 1,
          zIndex: 1,
          backgroundColor: "rgba(0, 0, 0, 0.90)",
          justifyContent: "center",
          alignItems: "center",
          paddingHorizontal: 20,
        }}
      >
        <View
          style={{
            width: "100%",
            backgroundColor: "#fff",
            // overflow: "hidden",
            shadowColor: "#000",
            shadowOffset: {
              width: 0,
              height: 0,
            },
            shadowOpacity: 0.25,
            shadowRadius: 10,
            borderRadius: 10,
            paddingHorizontal: 20,
          }}
        >
          <View
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              alignContent: "center",
              width: "100%",
            }}
          >
            <View
              style={{
                width: "100%",
                paddingTop: 20,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                alignContent: "center",
              }}
            >
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                  paddingBottom: 15,
                  borderBottomColor: Colors.black20,
                  borderBottomWidth: 0.5,
                }}
              >
                <View>
                  <CustomComponent.TextView
                    title="Choose date"
                    textStyle={[
                      GlobalStyles.heading3,
                      {
                        fontSize: 26,
                      },
                    ]}
                  />
                  <CustomComponent.TextView
                    title={
                      !selectedDate
                        ? "choose a date"
                        : isSlotAvailable?.slots > 0
                        ? `${isSlotAvailable?.slots} available`
                        : "no slots available"
                    }
                    textStyle={[
                      GlobalStyles.bodyText,
                      {
                        opacity: 0.6,
                      },
                    ]}
                  />
                </View>

                <Pressable onPress={toggleModal}>
                  <ExperienceIcons.Cross />
                </Pressable>
              </View>

              {/* <View
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  width: "100%",
                  gap:32,
                  marginLeft: 15,
                }}
              >
                {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                  <Text
                    key={index}
                    style={[GlobalStyles.bodyText, { opacity: 0.4 }]}
                  >
                    {day}
                  </Text>
                ))}
              </View> */}
              <View
                style={{
                  height: Dimensions.get("window").height / 2.3,
                  backgroundColor: "#000",
                  width: "100%",
                }}
              >
                <CalendarList
                  pastScrollRange={0}
                  hideDayNames={false}
                  hideExtraDays={true}
                  scrollEnabled={true}
                  onDayPress={onDayPress}
                  current={currentDate || undefined}
                  minDate={currentDate || undefined}
                  markingType={"custom"}
                  markedDates={getMarked()}
                  renderHeader={renderHeader}
                  calendarStyle={{
                    marginTop: 15,
                    marginLeft: -10,
                    width: Dimensions.get("window").width - 70,
                  }}
                  theme={{
                    textDayFontFamily: GlobalStyles.bodyText.fontFamily,
                    textMonthFontFamily: GlobalStyles.bodyText.fontFamily,
                  }}
                />
              </View>
              <View
                style={{
                  width: "100%",
                  paddingVertical: 20,
                  borderTopColor: Colors.black40,
                  borderTopWidth: 0.5,
                }}
              >
                <TouchableOpacity
                  style={[
                    GlobalStyles.primaryButton,
                    {
                      backgroundColor: Colors.darkGreen,
                      opacity: isSlotAvailable?.slots > 0 ? 1 : 0.4,
                      paddingVertical: 12,
                    },
                  ]}
                  onPress={() => {
                    isSlotAvailable?.slots > 0 ? setIsCard(true) : undefined;
                  }}
                >
                  <Text
                    style={[
                      GlobalStyles.ctaText,
                      {
                        color: "#FDFBF9",
                      },
                    ]}
                  >
                    Continue
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </BlurView>
    </ModalComponent>
  );
};

export default memo(DateModal);
